<script setup>
import { computed, nextTick, onBeforeUnmount, ref, useAttrs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DatePicker from './DatePicker.vue';
import TimeSlotPicker from './TimeSlotPicker.vue';
import {
  formatDisplayDate,
  formatDisplayDateTime,
  parseIsoDateTime,
  todayIsoDate,
} from './helpers';

const props = defineProps({
  modelValue: { type: String, default: null },
  id: { type: String, default: '' },
  accent: {
    type: String,
    default: 'blue',
    validator: value => ['blue', 'teal'].includes(value),
  },
  dateOnly: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  startHour: { type: Number, default: 8 },
  endHour: { type: Number, default: 20 },
  stepMinutes: { type: Number, default: 30 },
});
const emit = defineEmits(['update:modelValue']);

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const { t } = useI18n();

const open = ref(false);
const triggerRef = ref(null);
const popoverRef = ref(null);
const popoverStyle = ref({});

const close = () => {
  open.value = false;
};

const parsed = computed(() => parseIsoDateTime(props.modelValue));

const displayValue = computed(() =>
  props.dateOnly
    ? formatDisplayDate(parsed.value.date)
    : formatDisplayDateTime(props.modelValue)
);

const fieldPlaceholder = computed(() => {
  if (props.placeholder) return props.placeholder;
  return props.dateOnly
    ? t('DATE_PICKER.CHOOSE_DATE')
    : t('DATE_PICKER.CHOOSE_DATE_TIME');
});

const datePart = computed({
  get: () => parsed.value.date,
  set: iso => {
    if (props.dateOnly) {
      emit('update:modelValue', iso);
      if (iso) close();
      return;
    }
    if (!iso) {
      emit('update:modelValue', null);
      return;
    }
    emit('update:modelValue', `${iso}T${parsed.value.time || '08:00'}`);
  },
});

const timePart = computed({
  get: () => parsed.value.time,
  set: time => {
    if (!time) {
      emit('update:modelValue', null);
      return;
    }
    emit('update:modelValue', `${parsed.value.date || todayIsoDate()}T${time}`);
  },
});

const measure = () => {
  const trigger = triggerRef.value;
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  const popoverWidth = 288;
  const estimatedHeight = props.dateOnly ? 320 : 520;
  const spaceBelow = window.innerHeight - rect.bottom;
  const placeAbove = spaceBelow < estimatedHeight && rect.top > spaceBelow;
  const left = Math.min(
    Math.max(8, rect.left),
    Math.max(8, window.innerWidth - popoverWidth - 8)
  );

  popoverStyle.value = {
    position: 'fixed',
    zIndex: 2147483646,
    left: `${Math.round(left)}px`,
    width: `${popoverWidth}px`,
    ...(placeAbove
      ? { bottom: `${Math.round(window.innerHeight - rect.top + 8)}px` }
      : { top: `${Math.round(rect.bottom + 8)}px` }),
  };
};

const toggle = async () => {
  open.value = !open.value;
  if (open.value) {
    await nextTick();
    measure();
  }
};

const onDocumentMouseDown = event => {
  if (!open.value) return;
  if (triggerRef.value?.contains(event.target)) return;
  if (popoverRef.value?.contains(event.target)) return;
  close();
};

const onKeyDown = event => {
  if (event.key === 'Escape' && open.value) close();
};

watch(open, isOpen => {
  if (isOpen) {
    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
  } else {
    document.removeEventListener('mousedown', onDocumentMouseDown);
    document.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('resize', measure);
    window.removeEventListener('scroll', measure, true);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentMouseDown);
  document.removeEventListener('keydown', onKeyDown);
  window.removeEventListener('resize', measure);
  window.removeEventListener('scroll', measure, true);
});
</script>

<template>
  <div ref="triggerRef" class="relative w-full">
    <button
      :id="id || undefined"
      v-bind="attrs"
      type="button"
      class="flex h-11 w-full items-center rounded-xl border border-n-field bg-n-solid-1 hover:border-n-field-hover px-3 text-left text-sm tabular-nums"
      :aria-expanded="open"
      aria-haspopup="dialog"
      @click="toggle"
    >
      <span :class="displayValue ? 'text-n-slate-12' : 'text-n-slate-10'">
        {{ displayValue || fieldPlaceholder }}
      </span>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="popoverRef"
        role="dialog"
        aria-modal="true"
        :aria-label="fieldPlaceholder"
        class="flex flex-col gap-2"
        :style="popoverStyle"
      >
        <DatePicker v-model="datePart" :accent="accent" compact />
        <TimeSlotPicker
          v-if="!dateOnly"
          v-model="timePart"
          :accent="accent"
          :start-hour="startHour"
          :end-hour="endHour"
          :step-minutes="stepMinutes"
        />
      </div>
    </Teleport>
  </div>
</template>
